import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Spinner,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axiosInstance from "../utils/axiosInstance";

export default function CheckoutForm({ bookingPayload }) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBookingSuccessful, setIsBookingSuccessful] = useState(false);
  const bgColor = useColorModeValue("white", "black");
  const textColor = useColorModeValue("white", "white");

  const paymentElementOptions = { layout: "tabs" };

  useEffect(() => {
    if (!stripe) return;

    const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");

    if (!clientSecret) return;

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setMessage(error.message || "An unexpected error occurred.");
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      await handleBooking(paymentIntent.id);
    }

    setIsLoading(false);
  };

  const handleBooking = async (paymentIntentId) => {
    try {
      const backendUrl =
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BACKEND_URL
          : "http://localhost:8080";

      const response = await axiosInstance.post(
        `${backendUrl}/book`,
        {
          ...bookingPayload,
          paymentIntentId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        setIsBookingSuccessful(true);
      } else {
        setMessage("Booking could not be confirmed. Please contact support.");
      }
    } catch (error) {
      console.error("Error booking:", error);
      setMessage("Booking failed. Please try again later.");
    }
  };

  return (
    <Box p={6} bg={bgColor} borderRadius="xl" boxShadow="xl">
      {!isBookingSuccessful ? (
        <form id="payment-form" onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <PaymentElement id="payment-element" options={paymentElementOptions} />
            <Button
              colorScheme="purple"
              w="full"
              size="lg"
              borderRadius="full"
              isDisabled={isLoading || !stripe || !elements}
              type="submit"
            >
              {isLoading ? <Spinner size="sm" color="white" /> : "Pay Now"}
            </Button>
          </VStack>
        </form>
      ) : (
        <VStack spacing={4} textAlign="center">
          <Text fontSize="xl" fontWeight="bold" color="green.400">
            🎉 Booking made successfully!
          </Text>
          <Text fontSize="sm" color={textColor}>
            You will receive confirmation details shortly.
          </Text>
        </VStack>
      )}
      {message && (
        <Text fontSize="sm" color="red.400" mt={2} textAlign="center">
          {message}
        </Text>
      )}
    </Box>
  );
}
